import React, { createContext, useContext, useCallback } from 'react';
import { notifySuccess, notifyError, notifyWarning, notifyInfo, notify } from '../app/notifications';

type NotificationKind = 'success' | 'error' | 'warning' | 'info';

interface LegacyToastNotification {
  type: NotificationKind;
  title: string;
  message?: string;
  autoHide?: boolean;
  delay?: number;
}

interface NotificationContextType {
  showNotification: (notification: LegacyToastNotification) => void;
  showSuccess: (title: string, message?: string) => void;
  showError: (title: string, message?: string) => void;
  showWarning: (title: string, message?: string) => void;
  showInfo: (title: string, message?: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const composeMessage = (title: string, message?: string) => {
    return message ? `${title}: ${message}` : title;
  };

  const showNotification = useCallback((notification: LegacyToastNotification) => {
    const duration = notification.autoHide === false ? 0 : notification.delay ?? 2000;
    notify(notification.type, composeMessage(notification.title, notification.message), duration);
  }, []);

  const showSuccess = useCallback((title: string, message?: string) => {
    notifySuccess(composeMessage(title, message));
  }, []);

  const showError = useCallback((title: string, message?: string) => {
    notifyError(composeMessage(title, message), 2000);
  }, []);

  const showWarning = useCallback((title: string, message?: string) => {
    notifyWarning(composeMessage(title, message));
  }, []);

  const showInfo = useCallback((title: string, message?: string) => {
    notifyInfo(composeMessage(title, message));
  }, []);

  const value: NotificationContextType = {
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};